/**
 * @file Renders the save or delete action button for a transaction table row.
 */
import { Button } from "antd";
import { DeleteOutlined, SaveOutlined } from "@ant-design/icons";

import useInvestmentTransactionActions from "./helpers/useInvestmentTransactionActions";
import type { EditableTransaction, TransactionActions } from "./helpers/types";
import type { CollectionRef } from "../../types";

type Props = TransactionActions & {
  record: EditableTransaction;
  collection: CollectionRef;
  isDeletable: boolean;
};

/**
 * Renders the save action for the editable row or the delete action for a deletable transaction.
 */
const RowActions = ({
  record,
  collection,
  isDeletable,
  addTransactionAction,
  deleteTransactionAction,
  isInvestmentFundedTemplateEnabled,
}: Props) => {
  const { editableRow } = record;

  const {
    addTransaction,
    deleteTransaction,
  } = useInvestmentTransactionActions({
    record,
    addTransactionAction,
    deleteTransactionAction,
    isInvestmentFundedTemplateEnabled,
  });

  /**
   * Deletes the transaction row.
   */
  const onDelete = () =>
    deleteTransaction();

  /**
   * Saves the editable row after checking the collection form for errors.
   */
  const onSave = async () => {
    const isValid = await collection.current.hasFormError();

    if (!isValid) {
      return;
    }

    return addTransaction();
  };

  if (editableRow) {
    return (
      <Button
        type="link"
        icon={<SaveOutlined />}
        onClick={onSave}
      />
    );
  }

  if (isDeletable) {
    return (
      <Button
        type="link"
        icon={<DeleteOutlined />}
        danger={true}
        onClick={onDelete}
      />
    );
  }

  return null;
};

export default RowActions;
